import React, { useEffect, useState } from "react";
import { Box, Grid, Link, Typography } from "@mui/material";
import { fetchPublicCenterInfo } from "../api/apiClient";

const filled = (s) => typeof s === "string" && s.trim() !== "";

const Block = ({ testId, title, children }) => (
  <Grid item xs={12} md={4} data-testid={testId}>
    <Typography
      variant="overline"
      sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "var(--color-ink-2)" }}
    >
      {title}
    </Typography>
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Grid>
);

/**
 * Where / Who / About for the center in view. Renders nothing when no center is selected,
 * the request fails, or every block is empty.
 */
export default function PublicCenterInfo({ tenantId }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    setInfo(null);
    if (!tenantId) return undefined;
    let cancelled = false;
    fetchPublicCenterInfo(tenantId)
      .then((data) => !cancelled && setInfo(data))
      .catch(() => !cancelled && setInfo(null));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!info) return null;
  const people = (info.directory || []).filter((p) => filled(p.name));
  const showWhere = filled(info.physicalAddress) || filled(info.directions);
  const showAbout = filled(info.summary);
  if (!showWhere && !people.length && !showAbout) return null;

  return (
    <Box data-testid="center-info" sx={{ mt: 8 }}>
      <Grid container spacing={4}>
        {showWhere && (
          <Block testId="center-info-where" title="Where">
            {filled(info.physicalAddress) && (
              <Typography sx={{ whiteSpace: "pre-line" }}>{info.physicalAddress}</Typography>
            )}
            {filled(info.directions) && (
              <Typography sx={{ whiteSpace: "pre-line", mt: 1, color: "var(--color-ink-2)" }}>
                {info.directions}
              </Typography>
            )}
          </Block>
        )}
        {people.length > 0 && (
          <Block testId="center-info-who" title="Who">
            {people.map((p, i) => (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Typography fontWeight={700}>{p.name}</Typography>
                {filled(p.title) && (
                  <Typography variant="body2" sx={{ color: "var(--color-ink-2)" }}>{p.title}</Typography>
                )}
                {filled(p.email) && (
                  <Typography variant="body2">
                    <Link href={`mailto:${p.email}`}>{p.email}</Link>
                  </Typography>
                )}
                {filled(p.phone) && (
                  <Typography variant="body2">
                    <Link href={`tel:${p.phone}`}>{p.phone}</Link>
                  </Typography>
                )}
              </Box>
            ))}
          </Block>
        )}
        {showAbout && (
          <Block testId="center-info-about" title="About">
            <Typography sx={{ whiteSpace: "pre-line" }}>{info.summary}</Typography>
          </Block>
        )}
      </Grid>
    </Box>
  );
}
